/**
 * 메인 페이지
 */

import React from 'react';
import styled from '@emotion/styled';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { MainLayout } from '../../shared/components';
import { theme } from '../../shared/styles/theme';
import { mediaQueries } from '../../shared/constants/breakpoints';
import { ImageSlider, NaverMap } from './components';
import { mainService } from './services/mainService';

// 슬라이더 이미지 import
import slider1 from '../../assets/images/slider_1.JPG';
import slider2 from '../../assets/images/slider_1.JPG';
import slider3 from '../../assets/images/slider_1.JPG';

// 사용되지 않는 styled components (주석처리)
// const HeroSection = styled.section`
//   background: linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.secondary} 100%);
//   color: white;
//   padding: ${theme.spacing.xxxl} ${theme.spacing.lg};
//   text-align: center;
// `;

// const HeroTitle = styled.h1`
//   font-size: 48px;
//   font-weight: 700;
//   margin-bottom: ${theme.spacing.lg};
//   font-family: ${theme.fonts.heading};

//   ${mediaQueries.maxMobile} {
//     font-size: 32px;
//   }
// `;

// const HeroSubtitle = styled.p`
//   font-size: ${theme.fontSizes.xl};
//   margin-bottom: ${theme.spacing.xl};
//   opacity: 0.95;

//   ${mediaQueries.maxMobile} {
//     font-size: ${theme.fontSizes.lg};
//   }
// `;

// const ContentSection = styled.section`
//   max-width: 1200px;
//   margin: 0 auto;
//   padding: ${theme.spacing.xxxl} ${theme.spacing.lg};
// `;

const SectionTitle = styled.h2`
  font-size: ${theme.fontSizes.xxxl};
  font-weight: 700;
  text-align: center;
  margin-bottom: ${theme.spacing.xl};
  color: ${theme.colors.primary};
`;

// const WelcomeText = styled.p`
//   font-size: ${theme.fontSizes.lg};
//   line-height: 1.8;
//   text-align: center;
//   color: ${theme.colors.text};
//   max-width: 800px;
//   margin: 0 auto;
// `;

// 주보 및 예배시간 섹션 (2단 레이아웃)
const InfoSection = styled.section`
  background-color: #f8f9fa;
  padding: ${theme.spacing.xxxl} ${theme.spacing.lg};

  ${mediaQueries.maxMobile} {
    padding: ${theme.spacing.xl} ${theme.spacing.md};
  }
`;

const InfoInner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${theme.spacing.xl};

  ${mediaQueries.maxMobile} {
    grid-template-columns: 1fr;
    gap: ${theme.spacing.xl};
  }
`;

const InfoBox = styled.div`
  background-color: white;
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.xl};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  }

  ${mediaQueries.maxMobile} {
    padding: ${theme.spacing.md};
  }
`;

const InfoBoxTitle = styled.h3`
  font-size: ${theme.fontSizes.xl};
  font-weight: 700;
  color: ${theme.colors.primary};
  margin-bottom: ${theme.spacing.lg};
  padding-bottom: ${theme.spacing.sm};
  border-bottom: 3px solid ${theme.colors.primary};
  font-family: ${theme.fonts.heading};

  ${mediaQueries.maxMobile} {
    font-size: ${theme.fontSizes.lg};
    margin-bottom: ${theme.spacing.md};
  }
`;

// 주보/소식 테이블
const NewsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const NewsTableRow = styled.tr`
  border-bottom: 1px solid #e9ecef;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #f8f9fa;
  }

  &:last-child {
    border-bottom: none;
  }
`;

const NewsTableHeader = styled.th`
  padding: ${theme.spacing.md};
  text-align: left;
  font-size: ${theme.fontSizes.sm};
  font-weight: 700;
  color: white;
  background-color: ${theme.colors.primary};

  &:first-of-type {
    border-top-left-radius: ${theme.borderRadius.sm};
  }

  &:last-of-type {
    border-top-right-radius: ${theme.borderRadius.sm};
    text-align: center;

    @media (max-width: 600px) {
      display: none;
    }
  }
`;

const NewsTableCell = styled.td`
  padding: ${theme.spacing.md};
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.text};

  &:first-of-type {
    font-weight: 600;
    color: ${theme.colors.primary};
  }

  &:last-of-type {
    color: ${theme.colors.textLight};
    width: 120px;
    text-align: center;

    @media (max-width: 600px) {
      display: none;
    }
  }
`;

// 예배시간 안내 테이블
const WorshipTable = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const WorshipTableRow = styled.tr`
  border-bottom: 1px solid #e9ecef;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #f8f9fa;
  }

  &:last-child {
    border-bottom: none;
  }
`;

const WorshipTableHeader = styled.th`
  padding: ${theme.spacing.md};
  text-align: left;
  font-size: ${theme.fontSizes.sm};
  font-weight: 700;
  color: white;
  background-color: ${theme.colors.primary};

  &:first-of-type {
    border-top-left-radius: ${theme.borderRadius.sm};
  }

  &:last-of-type {
    border-top-right-radius: ${theme.borderRadius.sm};

    @media (max-width: 600px) {
      display: none;
    }
  }
`;

const WorshipTableCell = styled.td`
  padding: ${theme.spacing.md};
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.text};

  &:first-of-type {
    font-weight: 600;
    color: ${theme.colors.primary};
  }

  &:last-of-type {
    @media (max-width: 600px) {
      display: none;
    }
  }
`;


const MapSection = styled.section`
  padding: ${theme.spacing.xxxl} ${theme.spacing.lg};
  background-color: ${theme.colors.background};
`;

const MapInner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const MapContainer = styled.div`
  width: 100%;
  height: 450px;
  border-radius: ${theme.borderRadius.lg};
  overflow: hidden;
  box-shadow: ${theme.shadows.md};
  margin-top: ${theme.spacing.xl};

  ${mediaQueries.maxMobile} {
    height: 350px;
  }
`;

const ContactInfo = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: ${theme.spacing.lg};
  margin-top: ${theme.spacing.xl};
  padding: ${theme.spacing.xl};
  background-color: ${theme.colors.hover};
  border-radius: ${theme.borderRadius.lg};

  ${mediaQueries.maxMobile} {
    grid-template-columns: 1fr;
  }
`;

const ContactItem = styled.div`
  text-align: center;
`;

const ContactLabel = styled.p`
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.textLight};
  margin-bottom: ${theme.spacing.xs};
`;

const ContactValue = styled.p`
  font-size: ${theme.fontSizes.md};
  font-weight: 600;
  color: ${theme.colors.text};

  ${mediaQueries.maxMobile} {
    font-size: ${theme.fontSizes.sm};
  }
`;

// 사용되지 않는 styled components (주석처리)
// const LoadingContainer = styled.div`
//   display: flex;
//   justify-content: center;
//   align-items: center;
//   height: 400px;
//   font-size: ${theme.fontSizes.md};
//   color: ${theme.colors.textLight};

//   ${mediaQueries.maxTablet} {
//     height: 300px;
//   }

//   ${mediaQueries.maxMobile} {
//     height: 250px;
//   }
// `;

// const SliderPlaceholder = styled.div`
//   background: linear-gradient(135deg, #f0f0f0 0%, #e0e0e0 100%);
//   height: 500px;
//   display: flex;
//   justify-content: center;
//   align-items: center;
//   color: #999;
//   font-size: ${theme.fontSizes.md};

//   ${mediaQueries.maxTablet} {
//     height: 400px;
//   }

//   ${mediaQueries.maxMobile} {
//     height: 300px;
//   }
// `;

const HomePage: React.FC = () => {
  const navigate = useNavigate();

  // 교회 위치 좌표
  const churchLat = 37.4783;
  const churchLng = 127.1446;

  // 정적 슬라이더 이미지 데이터 (끊김 없는 빠른 로딩)
  const slides = [
    { id: 1, imageUrl: slider1, title: '', description: '' },
    { id: 2, imageUrl: slider2, title: '', description: '' },
    { id: 3, imageUrl: slider3, title: '', description: '' },
  ];

  // React Query로 최근 게시글 가져오기 (자동 캐싱)
  const { data: recentPostsData, isLoading: postsLoading } = useQuery({
    queryKey: ['recentPosts', 6],
    queryFn: async () => {
      const result = await mainService.getRecentPosts(6);
      return Array.isArray(result.posts) ? result.posts : [];
    },
  });

  const recentPosts = recentPostsData || [];

  // 게시글 클릭 핸들러
  const handlePostClick = (postId: number, boardType: string) => {
    // boardType에 따라 적절한 상세 페이지로 이동
    if (boardType === 'SUNDAY_SCHOOL') {
      navigate(`/sunday-school/board/${postId}`);
    } else {
      navigate(`/community/${boardType}/${postId}`);
    }
  };

  // 기존 슬라이더 API 로딩 코드 (주석처리)
  // useEffect(() => {
  //   const loadAllData = async () => {
  //     try {
  //       // 병렬 로딩으로 성능 개선
  //       const [slidesResult, postsResult] = await Promise.allSettled([
  //         sliderService.getSliders(),
  //         mainService.getRecentPosts(6)
  //       ]);

  //       // 슬라이더 데이터 처리
  //       if (slidesResult.status === 'fulfilled') {
  //         const data = slidesResult.value;
  //         if (Array.isArray(data)) {
  //           const sortedSlides = data.sort((a, b) => a.order - b.order);
  //           setSlides(sortedSlides);
  //         }
  //         setLoading(false);
  //       } else {
  //         console.error('Failed to load slider data:', slidesResult.reason);
  //         setError(slidesResult.reason);
  //         setLoading(false);
  //       }

  //       // 게시글 데이터 처리
  //       if (postsResult.status === 'fulfilled') {
  //         const data = postsResult.value;
  //         const postsArray = Array.isArray(data.posts) ? data.posts : [];
  //         setRecentPosts(postsArray);
  //         setPostsLoading(false);
  //       } else {
  //         console.error('Failed to load recent posts:', postsResult.reason);
  //         setPostsLoading(false);
  //       }
  //     } catch (err) {
  //       console.error('Unexpected error:', err);
  //       setError(err as Error);
  //       setLoading(false);
  //       setPostsLoading(false);
  //     }
  //   };

  //   loadAllData();
  // }, []);

  return (
    <MainLayout>
      {/* 정적 슬라이더 이미지 표시 (빠른 로딩) */}
      <ImageSlider slides={slides} />

      <InfoSection>
        <InfoInner>
          {/* 왼쪽: 예배 시간 안내 */}
          <InfoBox>
            <InfoBoxTitle>예배 시간 안내</InfoBoxTitle>
            <WorshipTable>
              <thead>
                <WorshipTableRow>
                  <WorshipTableHeader>예배명</WorshipTableHeader>
                  <WorshipTableHeader>시간</WorshipTableHeader>
                  <WorshipTableHeader>장소</WorshipTableHeader>
                </WorshipTableRow>
              </thead>
              <tbody>
                <WorshipTableRow>
                  <WorshipTableCell>주일 오전1부 예배</WorshipTableCell>
                  <WorshipTableCell>오전 09:00</WorshipTableCell>
                  <WorshipTableCell>본당</WorshipTableCell>
                </WorshipTableRow>
                <WorshipTableRow>
                  <WorshipTableCell>주일 오전2부 예배</WorshipTableCell>
                  <WorshipTableCell>오전 11:00</WorshipTableCell>
                  <WorshipTableCell>본당</WorshipTableCell>
                </WorshipTableRow>
                <WorshipTableRow>
                  <WorshipTableCell>주일 오후예배</WorshipTableCell>
                  <WorshipTableCell>오후 1:30</WorshipTableCell>
                  <WorshipTableCell>본당</WorshipTableCell>
                </WorshipTableRow>
                <WorshipTableRow>
                  <WorshipTableCell>수요예배</WorshipTableCell>
                  <WorshipTableCell>오후 7:30</WorshipTableCell>
                  <WorshipTableCell>본당</WorshipTableCell>
                </WorshipTableRow>
                <WorshipTableRow>
                  <WorshipTableCell>새벽기도회</WorshipTableCell>
                  <WorshipTableCell>오전 5:30</WorshipTableCell>
                  <WorshipTableCell>본당</WorshipTableCell>
                </WorshipTableRow>
                <WorshipTableRow>
                  <WorshipTableCell>금요성령집회</WorshipTableCell>
                  <WorshipTableCell>오후 9:00</WorshipTableCell>
                  <WorshipTableCell>본당</WorshipTableCell>
                </WorshipTableRow>
              </tbody>
            </WorshipTable>
          </InfoBox>

          {/* 오른쪽: 교회소식 및 갤러리 */}
          <InfoBox>
            <InfoBoxTitle>교회소식 및 갤러리</InfoBoxTitle>
            <NewsTable>
              <thead>
                <NewsTableRow>
                  <NewsTableHeader>제목</NewsTableHeader>
                  <NewsTableHeader>작성일</NewsTableHeader>
                </NewsTableRow>
              </thead>
              <tbody>
                {postsLoading ? (
                  <NewsTableRow>
                    <NewsTableCell colSpan={2} style={{ textAlign: 'center', color: '#999' }}></NewsTableCell>
                  </NewsTableRow>
                ) : recentPosts.length === 0 ? (
                  <NewsTableRow>
                    <NewsTableCell colSpan={2} style={{ textAlign: 'center' }}></NewsTableCell>
                  </NewsTableRow>
                ) : (
                  recentPosts.map((post) => (
                    <NewsTableRow key={post.id} onClick={() => handlePostClick(post.id, post.boardType)}>
                      <NewsTableCell>{post.title}</NewsTableCell>
                      <NewsTableCell>{new Date(post.createdAt).toLocaleDateString('ko-KR')}</NewsTableCell>
                    </NewsTableRow>
                  ))
                )}
              </tbody>
            </NewsTable>
          </InfoBox>
        </InfoInner>
      </InfoSection>

      <MapSection>
        <MapInner>
          <SectionTitle>오시는 길</SectionTitle>
          <ContactInfo>
            <ContactItem>
              <ContactLabel>주소</ContactLabel>
              <ContactValue>경기도 성남시 수정구 위례광장로 19 <span style={{ whiteSpace: 'nowrap' }}>아이페리온 8층</span></ContactValue>
            </ContactItem>
            <ContactItem>
              <ContactLabel>전화</ContactLabel>
              <ContactValue>0507-1427-6080</ContactValue>
            </ContactItem>
            <ContactItem>
              <ContactLabel>이메일</ContactLabel>
              <ContactValue>sewoomch@naver.com</ContactValue>
            </ContactItem>
          </ContactInfo>
          <MapContainer>
            <NaverMap lat={churchLat} lng={churchLng} title="위례세움교회" />
          </MapContainer>
        </MapInner>
      </MapSection>
    </MainLayout>
  );
};

export default HomePage;
